<template>
  <div class="single-page-markdown typ-layout">
    <h2>APM Key Details</h2>
    <table class="q-mb-md">
      <colgroup>
        <col style="width: 15.5465%" />
        <col style="width: 84.4674%" />
      </colgroup>
      <tbody>
        <tr>
          <td>Tech Lead</td>
          <td>
            <ul class="links q-px-none q-py-none q-mx-none q-my-none">
              <li class="q-mb-sm">
                <span style="text-transform: none">{{ apmDetails.itOwnerEmail }}</span>
              </li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>Business Lead/PO</td>
          <td>
            <ul class="links q-px-none q-py-none q-mx-none q-my-none">
              <li class="q-mb-sm">
                <span style="text-transform: none"> {{ apmDetails.businessOwnerEmail }}</span>
              </li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>APM Name</td>
          <td>
            <ul class="links q-px-none q-py-none q-mx-none q-my-none">
              <li class="q-mb-sm">
                <span style="text-transform: none">{{ dataService.displayName }}</span>
              </li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>APM ID</td>
          <td>
            <ul class="links q-px-none q-py-none q-mx-none q-my-none">
              <li class="q-mb-sm">
                <span style="text-transform: none">{{ apmDetails.apmId }}</span>
              </li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>Initial Release</td>
          <td>
            <ul class="links q-px-none q-py-none q-mx-none q-my-none">
              <li class="q-mb-sm">
                <span style="text-transform: none">{{ formatDate(apmDetails.createdAt) }}</span>
              </li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>Programming language(s)</td>
          <!-- https://dev.azure.com/{org}/{project}/_apis/projectanalysis/languagemetrics -->
          <!-- DGF Question 271 (What tech stack do you use?) could be a fallback yet only has 4 options: Java/JEE, Javascript, Microsoft based, Other -->
          <td>
            <span v-if="dataService.programmingLanguage">{{ dataService.programmingLanguage }}</span>
            <span v-else v-html="ownerRequest"></span>
          </td>
        </tr>
        <tr>
          <td>System Type</td>
          <td>
            <ul class="links q-px-none q-py-none q-mx-none q-my-none">
              <li class="q-mb-sm">
                <span style="text-transform: none">{{ dataService.type }}</span>
              </li>
            </ul>
          </td>
        </tr>
        <tr>
          <td>Project Site</td>
          <td>
            <span>Temporary Unavailable</span>
          </td>
        </tr>
        <tr>
          <td>Service Now</td>
          <td>
            <a
              v-if="dataService.links.find((link) => link.label === 'Service Now')?.url"
              :href="dataService.links.find((link) => link.label === 'Service Now')?.url"
              >Link</a
            >
            <span v-else v-html="ownerRequest"></span>
          </td>
        </tr>
        <tr>
          <td>Part of any shared service?</td>
          <td>Yes</td>
        </tr>
        <tr>
          <td>Associated Level 2 service</td>
          <td>N/A</td>
        </tr>
      </tbody>
    </table>
    <h1>Business Overview</h1>
    <div
      v-if="dataService.descriptions.find((desc) => desc.type === 'Overview')?.text?.length"
      class="single-page-markdown"
      v-html="dataMarkdown(dataService.descriptions.find((desc) => desc.type === 'Overview')?.text)"
    ></div>
    <p v-else v-html="ownerRequest"></p>
    <q-separator class="q-mb-md q-mt-sm" />
    <h1>Responsibilities</h1>
    <div
      v-if="dataService.descriptions.find((desc) => desc.type === 'Features')?.text?.length"
      class="single-page-markdown"
      v-html="dataMarkdown(dataService.descriptions.find((desc) => desc.type === 'Features')?.text)"
    ></div>
    <p v-else v-html="ownerRequest"></p>
    <q-separator class="q-mb-md q-mt-sm" />
    <h1>Current Release Information</h1>
    <a
      v-if="dataService.links.find((link) => link.label === 'Release Notes')?.url"
      target="_blank"
      :href="dataService.links.find((link) => link.label === 'Release Notes')?.url"
      >Go to page</a
    >
    <p v-else v-html="ownerRequest"></p>
    <q-separator class="q-mb-md q-mt-sm" />
    <h1>Collaborating Systems</h1>
    <h2>Systems We Depend On</h2>
    <span class="block q-mb-md">N/A</span>
    <h2>Systems We Enable</h2>
    <q-btn
      flat
      text-color="info"
      :ripple="false"
      no-caps
      style="min-height: 20px"
      text
      class="q-px-none q-py-none btn--no-hover"
      label="See all"
      @click="$emit('goToOnboardedServices')"
    ></q-btn>
    <q-separator class="q-mb-md q-mt-sm" />
    <h1>Contracts</h1>
    <h2>Published API</h2>
    <a
      v-if="dataService.links.find((link) => link.label === 'API Doc')?.url"
      target="_blank"
      :href="dataService.links.find((link) => link.label === 'API Doc')?.url"
      >Go to page</a
    >
    <span v-else class="block q-mb-md" v-html="ownerRequest"></span>
    <h2>Events</h2>
    <a
      v-if="dataService.links.find((link) => link.label === 'Published Events')?.url"
      target="_blank"
      :href="dataService.links.find((link) => link.label === 'Published Events')?.url"
      >Go to page</a
    >
    <span v-else class="block q-mb-md" v-html="ownerRequest"></span>
    <q-separator class="q-mb-md q-mt-sm" />
    <h1>Architecture</h1>
    <h2>System/Application Architecture</h2>
    <div
      v-if="dataService.descriptions.find((desc) => desc.type === 'Architecture')?.text?.length"
      class="single-page-markdown"
      v-html="dataMarkdown(dataService.descriptions.find((desc) => desc.type === 'Architecture')?.text)"
    ></div>
    <span v-else class="block q-mb-md" v-html="ownerRequest"></span>
    <h2>Technical/Platform Architecture</h2>
    <span class="block q-mb-md">N/A</span>
    <h2>Information Architecture</h2>
    <span class="block q-mb-md">N/A</span>
    <h2>Security Architecture</h2>
    <span class="block q-mb-md">N/A</span>
    <h2>Other Significant Architecture Perspectives</h2>
    <span class="block q-mb-md">N/A</span>
    <h2>Architecture Decision Records (ADRs)</h2>
    <q-btn
      flat
      text-color="info"
      :ripple="false"
      no-caps
      text
      style="min-height: 20px"
      class="q-px-none q-py-none btn--no-hover"
      label="See all"
      @click="$emit('goToAdrs')"
    ></q-btn>
    <q-separator class="q-mb-md q-mt-sm" />
    <h1>Developer Guides and Information</h1>
    <div
      v-if="dataService.descriptions.find((desc) => desc.type === 'Getting started/Consumption guide')?.text?.length"
      class="single-page-markdown"
      v-html="
        dataMarkdown(dataService.descriptions.find((desc) => desc.type === 'Getting started/Consumption guide')?.text)
      "
    ></div>
    <p v-else v-html="ownerRequest"></p>
    <q-separator class="q-mb-md q-mt-sm" />
    <h1>Permissions</h1>
    <p>N/A</p>
    <q-separator class="q-mb-md q-mt-sm" />
    <h1>Developing</h1>
    <h2>Repository Locations</h2>
    <a
      v-if="dataService.links.find((link) => link.label === 'Repository Link')?.url"
      target="_blank"
      :href="dataService.links.find((link) => link.label === 'Repository Link')?.url"
      >Go to page</a
    >
    <span v-else class="block q-mb-md" v-html="ownerRequest"></span>
    <h2>Local Development Environment</h2>
    <a
      v-if="dataService.links.find((link) => link.label === 'Developer onboarding')?.url"
      target="_blank"
      :href="dataService.links.find((link) => link.label === 'Developer onboarding')?.url"
      >Go to page</a
    >
    <span v-else class="block q-mb-md" v-html="ownerRequest"></span>
    <h2>Building &amp; Testing Locally</h2>
    <span class="block q-mb-md">N/A</span>
    <h2>Committing / Deploying The Solution</h2>
    <a
      v-if="dataService.links.find((link) => link.label === 'Wiki')?.url"
      target="_blank"
      :href="dataService.links.find((link) => link.label === 'Wiki')?.url"
      >Go to page</a
    >
    <span v-else class="block q-mb-md" v-html="ownerRequest"></span>
    <h2>Pipeline Location</h2>
    <span class="block q-mb-md">N/A</span>
    <h2>Code Level Documentation</h2>
    <span class="block q-mb-md">N/A</span>
    <h2>Non-Production Environments</h2>
    <span class="block q-mb-md">N/A</span>
    <h2>Production Environments</h2>
    <span class="block q-mb-md">N/A</span>
    <q-separator class="q-mb-md q-mt-sm" />
    <h1>Service Consumption</h1>
    <h2>Service Consumption Guide</h2>
    <div
      v-if="dataService.descriptions.find((desc) => desc.type === 'Getting started/Consumption guide')?.text?.length"
      class="single-page-markdown"
      v-html="
        dataMarkdown(dataService.descriptions.find((desc) => desc.type === 'Getting started/Consumption guide')?.text)
      "
    ></div>
    <span v-else class="block q-mb-md" v-html="ownerRequest"></span>
    <h2>Service Budgeting/Cost</h2>
    <div
      v-if="dataService.descriptions.find((desc) => desc.type === 'Cost')?.text?.length"
      class="single-page-markdown"
      v-html="dataMarkdown(dataService.descriptions.find((desc) => desc.type === 'Cost')?.text)"
    ></div>
    <span v-else class="block q-mb-md" v-html="ownerRequest"></span>
    <q-separator class="q-mb-md q-mt-sm" />
    <h1>Team Processes, Standards and Artifacts</h1>
    <h2>Backlog/Requirement Repository</h2>
    <a
      v-if="dataService.links.find((link) => link.label === 'Roadmap')?.url"
      target="_blank"
      :href="dataService.links.find((link) => link.label === 'Roadmap')?.url"
      >Go to page</a
    >
    <span v-else class="block q-mb-md" v-html="ownerRequest"></span>
    <h2>Issue Tracking System</h2>
    <a
      v-if="dataService.links.find((link) => link.label === 'Support Group')?.url"
      target="_blank"
      :href="dataService.links.find((link) => link.label === 'Support Group')?.url"
      >Go to page</a
    >
    <span v-else class="block q-mb-md" v-html="ownerRequest"></span>
    <h2>Team Operating Model</h2>
    <div
      v-if="dataService.descriptions.find((desc) => desc.type === 'Operating Model')?.text?.length"
      class="single-page-markdown"
      v-html="dataMarkdown(dataService.descriptions.find((desc) => desc.type === 'Operating Model')?.text)"
    ></div>
    <span v-else class="block q-mb-md" v-html="ownerRequest"></span>
    <h2>Team Member Details</h2>
    <ul class="team-members">
      <li><b>Business Owner:</b> {{ apmDetails.businessOwnerEmail }}</li>
      <li><b>It Owner:</b> {{ apmDetails.itOwnerEmail }}</li>
      <li><b>It Owner Custodian:</b> {{ apmDetails.itCustodianEmail }}</li>
      <li><b>Business Owner Custodian:</b> {{ apmDetails.businessOwnerCustodianEmail }}</li>
    </ul>
    <h2>Microsoft Teams Channel</h2>
    <a
      v-if="dataService.links.find((link) => link.label === 'Teams Channel')?.url"
      target="_blank"
      :href="dataService.links.find((link) => link.label === 'Teams Channel')?.url"
      >Go to page</a
    >
    <span v-else class="block q-mb-md" v-html="ownerRequest"></span>
    <h2>Project Web/Wiki Pages</h2>
    <a
      v-if="dataService.links.find((link) => link.label === 'Wiki')?.url"
      target="_blank"
      :href="dataService.links.find((link) => link.label === 'Wiki')?.url"
      >Go to page</a
    >
    <span v-else class="block q-mb-md" v-html="ownerRequest"></span>
    <p><span class="typ-version">TYP Template Version - 1.0.0-beta.7</span></p>
  </div>
</template>

<script>
import { marked } from 'marked';

export default {
  name: 'TechnicalYellowPage',
  props: {
    dataService: { type: Object, required: true },
    apmDetails: { type: Object, required: true },
  },
  computed: {
    ownerRequest() {
      return `<a href="mailto:${this.apmDetails.itOwnerEmail}">Request from Owner</a>`;
    },
    projectOwnerContacts() {
      return this.dataService.contacts.filter((contact) => contact.label == 'PROJECT OWNER');
    },
    pals() {
      return this.dataService.contacts.filter((contact) => contact.label == 'PAL');
    },
  },
  methods: {
    dataMarkdown(data) {
      return data ? marked(data) : '';
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      const day = date.getUTCDate();
      const month = date.toLocaleString('default', { month: 'long' });
      const year = date.getUTCFullYear();

      // Add suffix to day
      const daySuffix = (day) => {
        if (day > 3 && day < 21) return 'th'; // 11th, 12th, 13th
        switch (day % 10) {
          case 1:
            return 'st';
          case 2:
            return 'nd';
          case 3:
            return 'rd';
          default:
            return 'th';
        }
      };

      return `${day}${daySuffix(day)} of ${month} ${year}`;
    },
  },
};
</script>

<style lang="scss" scoped>
table {
  width: 100%;
  border-collapse: collapse;
}

td {
  padding: 8px;
  border: 1px solid #ddd;
}

h1,
h2,
h3 {
  margin-top: 10px;
  margin-bottom: 5px;
}
.typ-layout {
  h1 {
    margin-bottom: 10px;
  }
  h2 {
    font-size: 20px;
    margin-bottom: 5px;
  }
  h3 {
    font-size: 16px;
  }
  .typ-version {
    color: #a3a3a3;
  }
}
p {
  margin: 0;
}
.team-members {
  list-style-type: none;
  padding-left: 10px;
  b {
    display: inline-block;
    min-width: 180px;
  }
}
</style>
